import { Component } from 'react';
import PropTypes from 'prop-types';
import { graphql, createFragmentContainer } from 'react-relay/legacy';
import classNames from 'classnames';
import { connect } from 'react-redux';
import { ModalContainer } from 'dibs-elements/exports/ModalContainer';
import { ModalCloseButton } from 'dibs-elements/exports/ModalCloseButton';
import { BarHeader } from 'dibs-elements/exports/BarHeader';
import get from 'lodash.get';
import serverVars from 'server-vars';
import {
    setCurrentPage as setCurrentPageDispatch,
    setErrorOccurred as setErrorOccurredDispatch,
    togglePageOverview as togglePageOverviewDispatch,
    zoomOut as zoomOutDispatch,
} from '../actionCreators/uiActionCreators';
import { calculatedPageNumberDoubles, getCanvasDimensions, isSinglePage } from '../catalogHelpers';
import * as tracking from 'dibs-tracking';
import { FormattedMessage } from 'dibs-react-intl';

import styles from '../styles/catalogOverview.scss';
import commonStyles from '../styles/catalogCommon.scss';
import { TABLET, MOBILE } from '../../utils/deviceType';
import { PORTRAIT } from '../catalogConstants';

const CDN_HOST = serverVars.get('cdnDomain');

export class CatalogOverviewComponent extends Component {
    constructor(props) {
        super(props);

        this.state = {
            pagesRendered: null,
        };

        this.buildOverviewPageList = this.buildOverviewPageList.bind(this);
    }

    buildOverviewPageList() {
        const {
            catalogPage,
            currentPage,
            deviceType,
            screenOrientation,
            setCurrentPage,
            thumbnailScale,
            togglePageOverview,
            zoomOut,
        } = this.props;

        const pageCount = get(catalogPage, 'images.pageCount');
        const prefix = get(catalogPage, 'images.prefix');
        const singlePageDimensions = get(catalogPage, 'images.singlePageDimensions');
        const catalogTitle = get(catalogPage, 'title');
        const isMobile = deviceType === MOBILE;
        const isPortrait = screenOrientation === PORTRAIT;
        const displayPages = [];

        for (let j = 1; j <= pageCount; j++) {
            const isCurrSinglePage = isSinglePage(j, pageCount);
            let calculatedWidth = singlePageDimensions.width * thumbnailScale;

            if (!isCurrSinglePage) {
                calculatedWidth *= 2;
            }

            displayPages.push(
                <div
                    className={classNames({
                        [styles.overviewPageWrapper]: true,
                        [styles.overviewPageWrapperMobile]: isMobile,
                        [styles.overviewPageWrapperMobilePortrait]: isMobile && isPortrait,
                        [styles.overviewPageWrapperMobileLandscape]: isMobile && !isPortrait,
                        overviewContainer: true,
                    })}
                    key={`overview-page-wrapper-${j}`}
                >
                    <div
                        className={classNames({
                            [styles.thumbnailWrapper]: true,
                            [styles.thumbnailWrapperMobile]: isMobile,
                        })}
                        onClick={() => {
                            tracking.trackEvent({
                                category: 'product catalog',
                                action: 'image thumbnail clicked',
                                label: `${catalogTitle || 'n/a'}`,
                            });

                            // if this is a double page default to left page
                            const setPageLeft = !isCurrSinglePage;
                            setCurrentPage(j, setPageLeft, false);
                            zoomOut();

                            if (isMobile) {
                                togglePageOverview();
                            }
                        }}
                    >
                        <img
                            className={classNames({
                                [styles.overviewPage]: true,
                                [styles.currentOverviewPage]: j === currentPage,
                                [styles.notCurrentOverviewPage]: j !== currentPage,
                            })}
                            src={`${CDN_HOST}/pdf-editor/${prefix}_${j}.jpg?width=${Math.ceil(
                                calculatedWidth
                            )}`}
                        />
                    </div>
                    <div
                        className={classNames({
                            [styles.overviewPageNumber]: !isMobile,
                            [styles.overviewPageNumberMobile]: isMobile,
                            [commonStyles.proximaBold]: true,
                        })}
                    >
                        {/* always show page numbers as double on overview */}
                        {calculatedPageNumberDoubles(j, pageCount)}
                    </div>
                </div>
            );
        }

        return displayPages;
    }

    render() {
        const {
            catalogPage,
            deviceType,
            margin,
            mobileScale,
            pageOverviewOpen,
            scale,
            screenOrientation,
            singlePageRatio,
            tabletScale,
            togglePageOverview,
        } = this.props;

        const singlePageDimensions = get(catalogPage, 'images.singlePageDimensions');
        const isMobile = deviceType === MOBILE;

        const overviewContent = (
            <div
                className={classNames({
                    [styles.overviewPdfMobile]: isMobile,
                })}
            >
                {this.buildOverviewPageList()}
            </div>
        );

        if (isMobile) {
            const modalTitle = (
                <FormattedMessage id="CatalogOverview.modalTitle" defaultMessage="View Pages" />
            );
            return (
                <ModalContainer isOpen={pageOverviewOpen} onClose={togglePageOverview}>
                    <ModalCloseButton onClick={togglePageOverview} />
                    <BarHeader title={modalTitle} />
                    <div className={styles.overviewWrapperMobile}>{overviewContent}</div>
                </ModalContainer>
            );
        }

        let deviceScale = scale;
        if (deviceType === TABLET) {
            deviceScale = tabletScale;
        } else if (deviceType === MOBILE) {
            deviceScale = mobileScale;
        }

        const canvasDims = getCanvasDimensions(
            singlePageDimensions,
            deviceScale,
            margin,
            deviceType,
            screenOrientation,
            singlePageRatio
        );

        const canvasHeight = get(canvasDims, 'height') || 0;

        return (
            <div
                className={styles.overviewWrapper}
                style={{
                    display: this.props.pageOverviewOpen ? 'block' : 'none',
                    height: canvasHeight ? `${canvasHeight}px` : 'auto',
                }}
            >
                {overviewContent}
            </div>
        );
    }
}

CatalogOverviewComponent.propTypes = {
    catalogPage: PropTypes.object,
    tabletScale: PropTypes.number,
    currentPage: PropTypes.number,
    deviceType: PropTypes.string,
    margin: PropTypes.number,
    mobileScale: PropTypes.number,
    pageOverviewOpen: PropTypes.bool,
    scale: PropTypes.number,
    screenOrientation: PropTypes.string,
    setCurrentPage: PropTypes.func,
    setErrorOccurred: PropTypes.func,
    singlePageRatio: PropTypes.number,
    tableScale: PropTypes.number,
    thumbnailScale: PropTypes.number,
    togglePageOverview: PropTypes.func,
    zoomOut: PropTypes.func,
};

const mapStateToProps = state => {
    return {
        currentPage: state.ui.currentPage,
        margin: state.ui.margin,
        mobileScale: state.ui.mobileScale,
        pageOverviewOpen: state.ui.pageOverviewOpen,
        scale: state.ui.scale,
        screenOrientation: state.ui.screenOrientation,
        singlePageRatio: state.ui.singlePageRatio,
        tabletScale: state.ui.tabletScale,
        thumbnailScale: state.ui.thumbnailScale,
    };
};

export const mapDispatchToProps = dispatch => {
    return {
        setCurrentPage: (nextPageNum, pageL, pageR) =>
            dispatch(setCurrentPageDispatch(nextPageNum, pageL, pageR)),
        setErrorOccurred: () => dispatch(setErrorOccurredDispatch()),
        togglePageOverview: () => dispatch(togglePageOverviewDispatch()),
        zoomOut: () => dispatch(zoomOutDispatch()),
    };
};

export const CatalogOverview = createFragmentContainer(
    connect(mapStateToProps, mapDispatchToProps)(CatalogOverviewComponent),
    {
        catalogPage: graphql`
            fragment CatalogOverview_catalogPage on CatalogPage {
                title
                file {
                    filePath
                }
                images {
                    pageCount
                    prefix
                    singlePageDimensions {
                        height
                        width
                    }
                }
            }
        `,
    }
);
